import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useCampaign } from "./campaign-context";
import { ProfileCard } from "../../profile_utils";
import {
  useCreateCampaign,
  useUpdateCampaign,
  useGenerateCampaignTitle,
  useCampaignTags,
} from "./hooks";
import { InputTagging } from "../../../ui/input_tagging/input-tagging";
import { Campaign } from "./saved-campaigns-table";

interface SaveCampaignModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  campaign?: Campaign; // For editing
}

const getItemType = (type: string) => {
  switch (type) {
    case "candidats":
      return "Profile::Candidat";
    case "enseignes":
      return "Profile::Enseigne";
    case "villes":
      return "Profile::Ville";
    case "locations":
      return "Location";
    default:
      return type;
  }
};

const PLURAL_LABELS: Record<string, string> = {
  candidats: "candidats",
  enseignes: "enseignes",
  villes: "villes",
  locations: "emplacements",
};

function generateDefaultTitle(
  profiles: Array<{ type: string; entity: any }>,
): string {
  if (profiles.length === 0) return "Nouveau projet";

  // Group profiles by type
  const grouped: Record<string, string[]> = {};
  for (const p of profiles) {
    if (!grouped[p.type]) grouped[p.type] = [];
    const name = p.entity?.name;
    if (name) grouped[p.type].push(name);
  }

  const parts: string[] = [];
  for (const [type, names] of Object.entries(grouped)) {
    if (names.length === 1) {
      parts.push(names[0]);
    } else {
      parts.push(`${names.length} ${PLURAL_LABELS[type] || type}`);
    }
  }

  const title = parts.join(" - ");
  return title.length > 60 ? title.slice(0, 57) + "..." : title;
}

export function SaveCampaignModal({
  onClose,
  onSuccess,
  campaign,
}: SaveCampaignModalProps) {
  const { selectedProfiles, clearSelection } = useCampaign();

  const isEditMode = !!campaign;
  const defaultTitle = isEditMode
    ? campaign?.title || ""
    : generateDefaultTitle(selectedProfiles);

  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState(defaultTitle);
  const [tags, setTags] = useState<string[]>(campaign?.tags || []);

  const createCampaign = useCreateCampaign();
  const updateCampaign = useUpdateCampaign();
  const generateTitle = useGenerateCampaignTitle();
  const { data: existingTags = [] } = useCampaignTags();

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsOpen(true);
    });

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeydown);
    document.body.classList.add("overflow-hidden");

    return () => {
      document.removeEventListener("keydown", handleKeydown);
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  // Auto-generate a better title via LLM (only for new campaigns)
  useEffect(() => {
    if (!isEditMode && selectedProfiles.length > 0) {
      const items = selectedProfiles.map((profile) => ({
        id: profile.id,
        type: getItemType(profile.type),
      }));

      generateTitle.mutate(
        { items },
        {
          onSuccess: (generatedTitle) => {
            setTitle(generatedTitle);
          },
        },
      );
    }
  }, [isEditMode, selectedProfiles]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      return;
    }

    if (isEditMode) {
      // Update existing campaign
      updateCampaign.mutate(
        { id: campaign.id, title: title.trim(), tags },
        {
          onSuccess: () => {
            if (onSuccess) {
              onSuccess();
            }
            handleClose();
          },
        },
      );
    } else {
      // Create new campaign
      const items = selectedProfiles.map((profile) => ({
        id: profile.id,
        type: getItemType(profile.type),
      }));

      createCampaign.mutate(
        { title: title.trim(), tags, items },
        {
          onSuccess: () => {
            clearSelection();
            if (onSuccess) {
              onSuccess();
            }
            handleClose();
          },
        },
      );
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100000000] overflow-hidden">
      <div
        className={`absolute inset-0 bg-slate-800/70 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      />
      <div
        className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-lg shadow-xl flex flex-col transform transition-all duration-300 ${
          isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">
            {isEditMode ? "Modifier le projet" : "Enregistrer ce projet"}
          </h2>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-md hover:bg-slate-100 inline-flex items-center justify-center text-slate-400 hover:text-slate-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Title Input */}
          <div className="mb-6">
            <label
              htmlFor="campaign-title"
              className="block text-sm font-medium text-slate-700 mb-2"
            >
              Titre du projet
            </label>
            <div className="relative">
              <input
                id="campaign-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-default-500 focus:border-transparent"
                placeholder={
                  generateTitle.isPending
                    ? "Génération en cours..."
                    : "Mon projet de développement"
                }
                autoFocus
              />
              {generateTitle.isPending && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-slate-300 border-t-default-600 rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* Tags Input */}
          <div className="mb-6">
            <label
              htmlFor="campaign-tags"
              className="block text-sm font-medium text-slate-700 mb-2"
            >
              Tags (optionnel)
            </label>
            <InputTagging
              value={tags}
              options={existingTags}
              onChange={setTags}
              placeholder="Ajouter un tag pour regrouper les dossiers (exemple projet burgers)"
              multiple={true}
            />
            <p className="mt-1 text-xs text-slate-500">
              Ajouter un tag puis appuyer sur entrée
            </p>
          </div>

          {/* Error Message */}
          {(createCampaign.isError || updateCampaign.isError) && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              Une erreur est survenue lors de l'enregistrement
            </div>
          )}

          {/* Selected Profiles - Only show in create mode */}
          {!isEditMode && (
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">
                Profils sélectionnés ({selectedProfiles.length})
              </h3>
              <div className="space-y-2">
                {selectedProfiles.map((profile) => (
                  <ProfileCard
                    key={`${profile.type}-${profile.id}`}
                    entity={profile.entity}
                    entityType={profile.type}
                    selected={false}
                    disabled={false}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Campaign Items - Show in edit mode */}
          {isEditMode && campaign.campaign_items && (
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">
                Profils du projet ({campaign.campaign_items.length})
              </h3>
              <p className="text-sm text-slate-500">
                Note : La modification des profils n'est pas disponible. Vous
                pouvez uniquement modifier le titre et les tags.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            onClick={handleClose}
            disabled={createCampaign.isPending || updateCampaign.isPending}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={
              createCampaign.isPending ||
              updateCampaign.isPending ||
              !title.trim()
            }
            className="px-4 py-2 text-sm font-medium text-white bg-default-600 hover:bg-default-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createCampaign.isPending || updateCampaign.isPending
              ? "Enregistrement..."
              : isEditMode
                ? "Mettre à jour"
                : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
